/**
 * The stage state machine (§7).
 *
 * One definition, consumed by three places: the API enforces the exit
 * gates, the web app renders the stage rail from it, and the release
 * archive records which validations were green when each gate was passed.
 * Keeping it in one file is what stops the UI from claiming a stage is
 * complete when the API would refuse to advance it.
 */
export const STAGE_COUNT = 11; // stages 0..10
export const GATE_STATUS_LABELS = {
    not_started: 'Not started',
    in_progress: 'In progress',
    blocked: 'Blocked',
    awaiting_approval: 'Awaiting approval',
    passed: 'Passed',
    rejected: 'Rejected',
};
export const STAGES = [
    {
        stage: 0,
        key: 'purpose_scoping',
        name: 'Purpose & scoping',
        owner: 'preparer',
        ownerLabel: 'Preparer',
        summary: 'Capture purpose, basis, valuation date, holdings and route. Purpose validation locks the basis and every downstream option set.',
        entry: ['Engagement created'],
        validations: [
            { id: 'purpose_set', label: 'Purpose and basis of value captured', severity: 'blocking' },
            { id: 'basis_matches_purpose', label: 'Basis of value is permitted for the purpose', severity: 'blocking' },
            { id: 'valuation_date_set', label: 'Valuation date set and standards corpus pinned', severity: 'blocking' },
            { id: 'holding_present', label: 'At least one holding recorded', severity: 'blocking' },
            {
                id: 's161_considered',
                label: 'Related property (IHTA s.161) considered for every holding',
                severity: 'blocking',
                appliesWhen: 'fiscal',
            },
        ],
    },
    {
        stage: 1,
        key: 'data_ingestion',
        name: 'Data ingestion',
        owner: 'preparer',
        ownerLabel: 'Preparer + ingest worker',
        summary: 'Connectors, uploads, Companies House pull (accounts, PSC, charges, SH01s), IPO register, lease schedule. Documents parsed and financials mapped to the taxonomy.',
        entry: ['Stage 0 gate passed'],
        validations: [
            { id: 'entity_present', label: 'At least one entity recorded', severity: 'blocking' },
            { id: 'financials_present', label: 'Financial statements loaded for the valuation date', severity: 'warning' },
            { id: 'ch_pulled', label: 'Companies House record pulled for each registered entity', severity: 'warning' },
        ],
    },
    {
        stage: 2,
        key: 'financial_analysis',
        name: 'Financial analysis',
        owner: 'system',
        ownerLabel: 'System',
        summary: 'Automated pre-interview pass: margin decomposition, cost-centre clustering, asset-turnover benchmarking, surplus-cash test, freehold-at-cost detection and the share-transaction hunt. Produces hypotheses for the interview.',
        entry: ['Stage 1 gate passed'],
        validations: [
            { id: 'analysis_run', label: 'Automated analysis completed', severity: 'blocking' },
            {
                id: 'share_txn_hunt',
                label: 'Share-transaction hunt performed (SH01s, client records)',
                severity: 'blocking',
            },
        ],
    },
    {
        stage: 3,
        key: 'discovery',
        name: 'Discovery interview',
        owner: 'llm_human',
        ownerLabel: 'Preparer / client + agent',
        summary: 'Data-first interview opening from the Stage 2 hypotheses. Output is a business unit map and underutilised-asset sweep, every finding provenance-tagged.',
        entry: ['Stage 2 gate passed'],
        validations: [
            { id: 'units_defined', label: 'At least one valuation unit defined', severity: 'blocking' },
            {
                id: 'split_criteria_recorded',
                label: 'Every unit split records the criterion that justified it',
                severity: 'blocking',
            },
            { id: 'findings_tagged', label: 'Every finding carries a provenance tag', severity: 'blocking' },
            { id: 'underutilised_sweep', label: 'Underutilised-asset sweep completed', severity: 'warning' },
        ],
    },
    {
        stage: 4,
        key: 'methodology',
        name: 'Methodology selection',
        owner: 'llm_human',
        ownerLabel: 'Agent proposes, human approves',
        summary: 'Per valuation unit: rules engine plus agent rationale selects the basis. Recent arm’s-length share transactions force anchor-or-justify.',
        entry: ['Stage 3 gate passed'],
        validations: [
            { id: 'method_per_unit', label: 'Every valuation unit has a methodology', severity: 'blocking' },
            {
                id: 'anchor_or_justify',
                label: 'Recent arm’s-length transactions anchored, or the departure justified',
                severity: 'blocking',
            },
            // The anchor test counts written transactions, so a filed allotment
            // still sitting as a held reading is invisible to it. That is the one
            // shape of this gate passing on an incomplete answer: a lead the
            // system found, priced, and nobody looked at. Dismissing a reading
            // with a reason clears the check, so the judgement stays the
            // preparer's — only silence is refused.
            {
                id: 'allotment_readings_resolved',
                label: 'Every held allotment reading confirmed or dismissed',
                severity: 'blocking',
            },
            { id: 'method_approved', label: 'Methodology approved by a reviewer', severity: 'blocking' },
        ],
    },
    {
        stage: 5,
        key: 'evidence',
        name: 'Evidence assembly',
        owner: 'preparer',
        ownerLabel: 'Preparer + system',
        summary: 'Pull comparables, curves and discount studies from the evidence layer. Every number Stage 6 needs must exist as an evidence row or an approved manual input with a source.',
        entry: ['Stage 4 gate passed'],
        validations: [
            { id: 'inputs_sourced', label: 'Every calculation input has an evidence row or sourced manual input', severity: 'blocking' },
            { id: 'curves_current', label: 'Curves loaded as at the valuation date', severity: 'warning' },
        ],
    },
    {
        stage: 6,
        key: 'calculation',
        name: 'Calculation',
        owner: 'calc',
        ownerLabel: 'Calculation engine',
        summary: 'Deterministic. Maintainable earnings, multiple application, DCF, NAV assembly, instrument pricing and pension roll-forward, with a full calculation log.',
        entry: ['Stage 5 gate passed'],
        validations: [
            { id: 'calc_run_per_unit', label: 'A calculation run exists for every valuation unit', severity: 'blocking' },
            { id: 'no_calc_errors', label: 'No calculation run in error', severity: 'blocking' },
        ],
    },
    {
        stage: 7,
        key: 'adjustments',
        name: 'Adjustments & bridge',
        owner: 'reviewer',
        ownerLabel: 'Calculation engine + reviewer',
        summary: 'Enterprise-to-equity bridge, working-capital normalisation, purpose-aware latent tax, evidence-backed DLOC/DLOM, and the sum-of-the-parts group bridge. Holding-level resolution for fiscal engagements.',
        entry: ['Stage 6 gate passed'],
        validations: [
            { id: 'no_open_corridor_exceptions', label: 'No corridor exception left unapproved', severity: 'blocking' },
            {
                id: 'holding_resolved',
                label: 'Resolved to a value per holding at the top entity',
                severity: 'blocking',
                appliesWhen: 'fiscal',
            },
            { id: 'discount_evidence', label: 'Every discount applied is backed by an evidence range', severity: 'blocking' },
        ],
    },
    {
        stage: 8,
        key: 'draft_report',
        name: 'Draft report',
        owner: 'llm_human',
        ownerLabel: 'Agent + templates',
        summary: 'Narrative generated section by section against the Acumon templates. Every figure interpolates from the calculation log; every standards claim carries a citation.',
        entry: ['Stage 7 gate passed'],
        validations: [
            { id: 'figures_bound', label: 'Every report figure binds to a calculation step', severity: 'blocking' },
            { id: 'claims_cited', label: 'Every standards claim cites a corpus reference', severity: 'blocking' },
            { id: 'mandatory_sections', label: 'Mandatory sections for the purpose are present', severity: 'blocking' },
            { id: 'reliance_appendix', label: 'Reliance appendix generated from the register', severity: 'blocking' },
        ],
    },
    {
        stage: 9,
        key: 'review',
        name: 'Review gate',
        owner: 'reviewer',
        ownerLabel: 'Route-appropriate reviewer',
        summary: 'Checklist-driven review: exceptions first — corridor breaches, provenance weaknesses, uncited claims — then the full report. Rejection returns the engagement to the relevant stage.',
        entry: ['Stage 8 gate passed'],
        validations: [
            { id: 'exceptions_cleared', label: 'All exceptions reviewed', severity: 'blocking' },
            {
                id: 'reliance_scope_valid',
                label: 'External valuation reliance scope covers client and HMRC disclosure',
                severity: 'blocking',
                appliesWhen: 'fiscal',
            },
            { id: 'reviewer_approval', label: 'Route-appropriate reviewer has approved', severity: 'blocking' },
        ],
    },
    {
        stage: 10,
        key: 'release',
        name: 'Release & archive',
        owner: 'system',
        ownerLabel: 'System',
        summary: 'Final render, then an immutable archive of report, calculation log, evidence snapshot and corpus pins — the SAV-negotiation and PI-defence record.',
        entry: ['Stage 9 gate passed'],
        validations: [
            { id: 'release_approved', label: 'Release approved by the required role', severity: 'blocking' },
            { id: 'archive_written', label: 'Defence file archived', severity: 'blocking' },
        ],
    },
];
export function stageDef(stage) {
    const def = STAGES[stage];
    if (!def)
        throw new Error(`Unknown stage ${stage}`);
    return def;
}
/**
 * What kind of thing an item is, which is how the UI knows where it lives.
 * `action` is the case where nothing exists yet to point at — the check is
 * failing because a step has not been taken rather than because a particular
 * row is wrong.
 */
export const VALIDATION_ITEM_KINDS = [
    'entity',
    'holding',
    'valuation_unit',
    'methodology',
    'finding',
    'allotment_reading',
    'exception',
    'calc_run',
    'external_valuation',
    'report_section',
    'action',
];
/** A gate opens when nothing blocking is failing. Warnings never block. */
export function canPassGate(results) {
    return !results.some((r) => r.severity === 'blocking' && !r.passed);
}
export function blockingFailures(results) {
    return results.filter((r) => r.severity === 'blocking' && !r.passed);
}
//# sourceMappingURL=workflow.js.map